import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  X, Wifi, WifiOff, Plus, Trash2, Edit2, Power, 
  Thermometer, Lightbulb, Lock, AlertTriangle, RefreshCw 
} from 'lucide-react';
import './RoomDetailsModal.css';

const DEVICE_ICONS = {
  lighting: Lightbulb,
  climate: Thermometer,
  security: Lock,
  connectivity: Wifi,
  default: Power
};

const RoomDetailsModal = ({ room, onClose, onUpdate, token }) => {
  const [devices, setDevices] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const IOT_API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (room) {
      loadRoomDevices();
    }
  }, [room]);

  const loadRoomDevices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${IOT_API}/iot-manager/devices?roomId=${room.id}`,
        auth
      );
      if (res.data.success) {
        setDevices(res.data.data || []);
      }
    } catch (err) {
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDevices = async () => {
    try {
      setRefreshing(true);
      // Discover SmartThings devices
      const res = await axios.get(
        `${IOT_API}/iot-manager/smartthings/discover`,
        auth
      );
      if (res.data.success) {
        // Filter out devices already assigned to this or other rooms
        const unassigned = (res.data.data.devices || []).filter(d => !d.isLinked);
        setAvailableDevices(unassigned);
      }
    } catch (err) {
      console.error('Error discovering devices:', err);
      alert('Failed to discover SmartThings devices');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddDevice = async () => {
    if (!selectedDevice) return;

    try {
      // Import the device and assign to this room
      const res = await axios.post(
        `${IOT_API}/iot-manager/smartthings/import`,
        { 
          device_ids: [selectedDevice.id],
          roomId: room.id  // Assign to this room
        },
        auth
      );

      if (res.data.success) {
        alert(`Device "${selectedDevice.name}" added successfully!`);
        setShowAddDevice(false);
        setSelectedDevice(null);
        loadRoomDevices();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error adding device:', err);
      alert(err.response?.data?.message || 'Failed to add device');
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    if (!window.confirm('Remove this device from the room?')) return;

    try {
      await axios.delete(
        `${IOT_API}/iot-manager/devices/${deviceId}`,
        auth
      );
      alert('Device removed successfully');
      loadRoomDevices();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error removing device:', err);
      alert('Failed to remove device');
    }
  };

  const handleControlDevice = async (device, command) => {
    try {
      const res = await axios.post(
        `${IOT_API}/iot-manager/devices/${device.device_id}/command`,
        { command },
        auth
      );
      if (res.data.success) {
        alert(`Command sent successfully`);
        loadRoomDevices();
      }
    } catch (err) {
      console.error('Error controlling device:', err);
      alert('Failed to send command');
    }
  };

  const getDeviceIcon = (category) => {
    const Icon = DEVICE_ICONS[category] || DEVICE_ICONS.default;
    return <Icon size={20} />;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'online': return '#4caf50';
      case 'offline': return '#f44336';
      case 'error': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="room-details-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Room {room.room_number}</h2>
            <p className="room-subtitle">
              {room.room_type} • Floor {room.floor_id}
            </p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Room Info Section */}
          <div className="info-section">
            <h3>Room Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Status:</span>
                <span className={`status-badge ${room.status}`}>{room.status}</span>
              </div>
              <div className="info-item">
                <span className="label">Type:</span>
                <span>{room.room_type}</span>
              </div>
              <div className="info-item">
                <span className="label">Floor:</span>
                <span>Floor {room.floor_id}</span>
              </div>
              <div className="info-item">
                <span className="label">Devices:</span>
                <span>{devices.length} connected</span>
              </div>
            </div>
          </div>

          {/* Devices Section */}
          <div className="devices-section">
            <div className="section-header">
              <h3>IoT Devices ({devices.length})</h3>
              <button 
                className="btn-add-device"
                onClick={() => {
                  setShowAddDevice(true);
                  loadAvailableDevices();
                }}
              >
                <Plus size={16} />
                Add Device
              </button>
            </div>

            {loading ? (
              <div className="loading">
                <RefreshCw className="spinning" size={24} />
                <p>Loading devices...</p>
              </div>
            ) : devices.length === 0 ? (
              <div className="no-devices">
                <Wifi size={48} />
                <p>No devices in this room</p>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddDevice(true);
                    loadAvailableDevices();
                  }}
                >
                  Add First Device
                </button>
              </div>
            ) : (
              <div className="devices-list">
                {devices.map(device => (
                  <div key={device.device_id} className="device-card">
                    <div className="device-icon" style={{ color: getStatusColor(device.status) }}>
                      {getDeviceIcon(device.category)}
                    </div>
                    <div className="device-info">
                      <h4>{device.device_name}</h4>
                      <p>{device.device_type}</p>
                      <span className={`device-status ${device.status}`}>
                        {device.status === 'online' ? <Wifi size={12} /> : <WifiOff size={12} />}
                        {device.status}
                      </span>
                    </div>
                    <div className="device-actions">
                      {device.status === 'online' && device.category === 'lighting' && (
                        <button
                          className="btn-control"
                          onClick={() => handleControlDevice(device, 'toggle')}
                          title="Toggle"
                        >
                          <Power size={16} />
                        </button>
                      )}
                      <button
                        className="btn-remove"
                        onClick={() => handleRemoveDevice(device.device_id)}
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add Device Modal */}
        {showAddDevice && (
          <div className="add-device-overlay" onClick={() => setShowAddDevice(false)}>
            <div className="add-device-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add SmartThings Device</h3>
                <button onClick={() => setShowAddDevice(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body">
                {refreshing ? (
                  <div className="loading">
                    <RefreshCw className="spinning" size={32} />
                    <p>Discovering devices...</p>
                  </div>
                ) : availableDevices.length === 0 ? (
                  <div className="no-devices">
                    <AlertTriangle size={48} />
                    <p>No unassigned devices found</p>
                    <button 
                      className="btn-secondary"
                      onClick={loadAvailableDevices}
                    >
                      <RefreshCw size={16} />
                      Refresh
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="available-devices-list">
                      {availableDevices.map(device => (
                        <div
                          key={device.id}
                          className={`available-device ${selectedDevice?.id === device.id ? 'selected' : ''}`}
                          onClick={() => setSelectedDevice(device)}
                        >
                          <div className="device-icon">
                            {getDeviceIcon(device.category)}
                          </div>
                          <div className="device-info">
                            <h4>{device.name}</h4>
                            <p>{device.type}</p>
                            <span className="device-category">{device.category}</span>
                          </div>
                          {selectedDevice?.id === device.id && (
                            <div className="selected-check">✓</div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="modal-actions">
                      <button 
                        className="btn-cancel"
                        onClick={() => setShowAddDevice(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-primary"
                        disabled={!selectedDevice}
                        onClick={handleAddDevice}
                      >
                        Add Device
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomDetailsModal;
